import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Menu, Search } from 'lucide-react'
import { useAuth } from '../../hooks/use-auth'
import { useIsRailDrawer } from '../../hooks/use-is-rail-drawer'
import { useBodyScrollLock } from '../../hooks/use-body-scroll-lock'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { BrandLogo } from '../shared/brand-logo'
import { Rail } from './rail'

interface AppShellProps {
  children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function AppShell({ children }: AppShellProps) {
  const [navigationOpen, setNavigationOpen] = useState(false)
  const { user } = useAuth()
  const { railExpanded, toggleCommandPalette } = useAppStore()
  const drawerMode = useIsRailDrawer()
  const navRef = useRef<HTMLElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  useBodyScrollLock(drawerMode && navigationOpen)

  const closeNavigation = (restoreFocus = false) => {
    setNavigationOpen(false)
    if (restoreFocus) triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!drawerMode || !navigationOpen) return
    const nav = navRef.current
    const focusable = Array.from(nav?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    focusable.find((item) => item.dataset.railDestination === 'true')?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeNavigation(true)
        return
      }
      if (event.key !== 'Tab' || focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [drawerMode, navigationOpen])

  useEffect(() => {
    if (!drawerMode) setNavigationOpen(false)
  }, [drawerMode])

  return (
    <div
      className={cn(
        'aw-app-shell',
        railExpanded && 'rail-open',
        drawerMode && 'rail-drawer',
        navigationOpen && 'nav-open',
      )}
    >
      {drawerMode && (
        <header
          className="aw-global-bar"
          aria-hidden={navigationOpen || undefined}
          inert={navigationOpen ? true : undefined}
        >
          <button
            ref={triggerRef}
            type="button"
            className="aw-global-bar-button"
            aria-label="Open navigation"
            aria-expanded={navigationOpen}
            aria-controls="global-navigation"
            onClick={() => setNavigationOpen(true)}
          >
            <Menu aria-hidden="true" />
          </button>
          <BrandLogo size="xs" />
          <button
            type="button"
            className="aw-global-bar-button"
            aria-label="Open command palette"
            aria-keyshortcuts="Control+K Meta+K"
            onClick={toggleCommandPalette}
          >
            <Search aria-hidden="true" />
          </button>
        </header>
      )}

      {drawerMode && (
        <div
          className="aw-rail-backdrop"
          aria-hidden="true"
          onClick={() => closeNavigation(true)}
        />
      )}
      <Rail
        userName={user?.name}
        drawerMode={drawerMode}
        drawerOpen={navigationOpen}
        navRef={navRef}
        onNavigate={drawerMode ? () => closeNavigation(true) : undefined}
        onClose={drawerMode ? () => closeNavigation(true) : undefined}
      />
      <main
        data-shell-content
        tabIndex={-1}
        className="aw-shell-content"
        aria-hidden={drawerMode && navigationOpen ? true : undefined}
        inert={drawerMode && navigationOpen ? true : undefined}
      >
        {children}
      </main>
    </div>
  )
}
